/* holy-search.js — S3 header search overlay.
   Flow: normalize query → expand via holy-search-terms.json (substring-matched, grep-like)
   → Shopify predictive search (/search/suggest.json) on raw + expanded terms
   → pinned catalog products (/products/<handle>.js) → merge, dedupe, render.
   Every card carries a quiet reason line: dictionary matches use the alias's 'r' string
   from the JSON; raw predictive hits use the JSON 'why' templates ("matches ‘q’ in the name").
   Open is jank-free: scrollbar width is compensated before the scroll lock and focus lands
   after paint (preventScroll). Debounced · keyboard accessible (arrows + Enter) · ESC closes.
   No hardcoded content: strings come from section settings via data-attrs; vocabulary +
   reason wording live in the JSON asset. */
(function () {
  var ov = document.getElementById('s3-searchov');
  if (!ov) return;
  var input = ov.querySelector('[data-s3-search-input]');
  var resultsEl = ov.querySelector('[data-s3-search-results]');
  var hintEl = ov.querySelector('[data-s3-search-hint]');
  var liveEl = ov.querySelector('[data-s3-search-live]');
  var TERMS_URL = ov.getAttribute('data-terms-url') || '';
  var EMPTY_TEXT = ov.getAttribute('data-empty-text') || 'Nothing yet — try an ingredient or a step.';
  var HINT_TEXT = hintEl ? hintEl.textContent : '';
  var DICT = null, dictLoading = null, prodCache = {}, seq = 0, debounceT = null, lastQ = '';

  /* ---------- open / close ---------- */
  function openOv() {
    /* reserve the scrollbar's width BEFORE locking scroll so nothing behind shifts */
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--s3-sbw', (sbw > 0 ? sbw : 0) + 'px');
    ov.hidden = false;
    document.documentElement.classList.add('s3-search-on');
    loadDict();
    /* focus only after the overlay has painted; preventScroll stops iOS pull-to-input */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      });
    });
  }
  function closeOv() {
    ov.hidden = true;
    document.documentElement.classList.remove('s3-search-on');
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-s3-search]')) { e.preventDefault(); openOv(); return; }
    if (e.target.closest('[data-s3-search-close]')) { e.preventDefault(); closeOv(); return; }
  });
  document.addEventListener('keydown', function (e) {
    if (ov.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); closeOv(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      var links = [].slice.call(resultsEl.querySelectorAll('a'));
      if (!links.length) return;
      var i = links.indexOf(document.activeElement);
      var next = e.key === 'ArrowDown' ? Math.min(i + 1, links.length - 1) : (i <= 0 ? -1 : i - 1);
      e.preventDefault();
      if (next < 0) input.focus(); else links[next].focus();
    }
  });

  /* ---------- dictionary ---------- */
  function loadDict() {
    if (DICT || dictLoading || !TERMS_URL) return;
    dictLoading = fetch(TERMS_URL).then(function (r) { return r.json(); }).then(function (j) { DICT = j; })
      .catch(function () { DICT = { terms: {}, links: {} }; });
  }
  function norm(q) {
    return (q || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w\s%+-]/g, ' ').replace(/\s+/g, ' ').trim();
  }
  /* grep-like: an alias matches if the full query contains it, or any query word
     ≥3 chars is a substring of the alias (so "moistur" hits "moisturizer"). */
  function expand(q) {
    var out = { qs: [], handles: [], links: [], qWhy: {}, pinWhy: {} };
    if (!DICT) return out;
    var words = q.split(' ').filter(function (w) { return w.length >= 3; });
    function matches(alias) {
      if (q.indexOf(alias) !== -1 || alias.indexOf(q) !== -1) return true;
      for (var i = 0; i < words.length; i++) {
        if (alias.indexOf(words[i]) !== -1) return true;
      }
      return false;
    }
    var terms = DICT.terms || {};
    for (var alias in terms) {
      if (!matches(alias)) continue;
      var r = terms[alias].r || '';
      (terms[alias].q || []).forEach(function (x) { if (out.qs.indexOf(x) < 0 && x !== q) out.qs.push(x); if (r && !out.qWhy[x]) out.qWhy[x] = r; });
      (terms[alias].p || []).forEach(function (h) { if (out.handles.indexOf(h) < 0) out.handles.push(h); if (r && !out.pinWhy[h]) out.pinWhy[h] = r; });
    }
    var links = DICT.links || {};
    for (var lk in links) {
      if (!matches(lk)) continue;
      links[lk].forEach(function (l) {
        if (!out.links.some(function (x) { return x.u === l.u; })) out.links.push(l);
      });
    }
    return out;
  }

  /* ---------- data fetch ---------- */
  function fetchSuggest(q) {
    var u = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=8' +
      '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag,body';
    return fetch(u).then(function (r) { return r.json(); })
      .then(function (j) { return ((j.resources || {}).results || {}).products || []; })
      .catch(function () { return []; });
  }
  function fetchProduct(h) {
    if (prodCache[h]) return prodCache[h];
    prodCache[h] = fetch('/products/' + h + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    return prodCache[h];
  }

  /* ---------- render ---------- */
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function money(v, isCents) {
    var n = isCents ? v / 100 : parseFloat(v);
    if (isNaN(n)) return '';
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n); }
    catch (e) { return '$' + n.toFixed(2); }
  }
  /* reason wording — templates live in the terms JSON ("why": {match, fields}) */
  function whyTpl() { return (DICT && DICT.why) || {}; }
  function whyFrom(q, field) {
    var w = whyTpl(); if (!w.match) return '';
    var f = (w.fields || {})[field] || '';
    return w.match.replace('{q}', q).replace('{f}', f).replace(/\s+/g, ' ').trim();
  }
  function whyRaw(p, q) {
    var title = (p.title || '').toLowerCase();
    var tags = typeof p.tags === 'string' ? p.tags.toLowerCase() : ((p.tags || []).join(', ').toLowerCase());
    var type = (p.type || '').toLowerCase();
    if (title.indexOf(q) !== -1) return whyFrom(q, 'title');
    if (tags.indexOf(q) !== -1) return whyFrom(q, 'tag');
    if (type.indexOf(q) !== -1) return whyFrom(q, 'type');
    return whyFrom(q, 'fallback');
  }
  function stepFromTags(tags) {
    if (!tags) return '';
    var arr = typeof tags === 'string' ? tags.split(', ') : tags;
    for (var i = 0; i < arr.length; i++) {
      var m = /^step:(.+)$/i.exec(arr[i]);
      if (m) return m[1].toLowerCase();
    }
    return '';
  }
  function card(item) {
    var step = item.step ? '<span class="s3-sr__mark">høly' + esc(item.step) + '</span>' : (item.type ? '<span class="s3-sr__mark s3-sr__mark--type">' + esc(item.type) + '</span>' : '');
    return '<a class="s3-sr" href="' + esc(item.url) + '" role="option">' +
      '<span class="s3-sr__img">' + (item.img ? '<img src="' + esc(item.img) + '" alt="" loading="lazy">' : '') + '</span>' +
      '<span class="s3-sr__meta">' + step +
      '<span class="s3-sr__name">' + esc(item.title) + '</span>' +
      (item.price ? '<span class="s3-sr__price">' + esc(item.price) + '</span>' : '') +
      (item.why ? '<span class="s3-sr__why">' + esc(item.why) + '</span>' : '') +
      '</span></a>';
  }
  function sized(u, w) {
    if (!u) return '';
    return u.indexOf('cdn.shopify.com') !== -1 ? u.replace(/(\.[a-z]{3,4})(\?|$)/, '_' + w + 'x$1$2') : u;
  }
  function fromSuggest(p) {
    return { handle: p.handle, url: p.url, title: p.title, type: p.type || '',
      step: stepFromTags(p.tags), img: sized((p.featured_image && p.featured_image.url) || p.image, 400),
      price: money(p.price, false) };
  }
  function fromProductJs(p) {
    if (!p) return null;
    return { handle: p.handle, url: '/products/' + p.handle, title: p.title, type: p.type || '',
      step: stepFromTags(p.tags), img: sized(p.featured_image, 400), price: money(p.price, true) };
  }
  function render(items, links, q) {
    var html = '';
    if (links.length) {
      html += '<div class="s3-sr__links">' + links.map(function (l) {
        return '<a class="s3-sr__link" href="' + esc(l.u) + '">' + esc(l.t) + ' →</a>';
      }).join('') + '</div>';
    }
    if (items.length) {
      html += '<div class="s3-sr__grid" role="listbox">' + items.map(card).join('') + '</div>';
      html += '<a class="s3-sr__all" href="/search?q=' + encodeURIComponent(q) + '&type=product">See all results →</a>';
    } else if (q) {
      html += '<p class="s3-sr__none">' + esc(EMPTY_TEXT) + '</p>';
    }
    resultsEl.innerHTML = html;
    if (hintEl) hintEl.hidden = !!(items.length || links.length);
    if (liveEl) liveEl.textContent = q ? items.length + ' results' : '';
  }

  /* ---------- orchestration ---------- */
  function run(qRaw) {
    var q = norm(qRaw);
    lastQ = q;
    if (q.length < 2) { resultsEl.innerHTML = ''; if (hintEl) { hintEl.hidden = false; hintEl.textContent = HINT_TEXT; } if (liveEl) liveEl.textContent = ''; return; }
    var mySeq = ++seq;
    var go = function () {
      var ex = expand(q);
      var queries = [q].concat(ex.qs.slice(0, 3));
      var pinned = ex.handles.slice(0, 12);
      Promise.all([
        Promise.all(queries.map(fetchSuggest)),
        Promise.all(pinned.map(fetchProduct))
      ]).then(function (res) {
        if (mySeq !== seq || q !== lastQ) return; /* stale */
        var seen = {}, items = [];
        function push(it, why) { if (it && it.handle && !seen[it.handle]) { seen[it.handle] = 1; if (why) it.why = why; items.push(it); } }
        (res[0][0] || []).forEach(function (p) { push(fromSuggest(p), whyRaw(p, q)); });                       /* raw-query hits first */
        res[1].forEach(function (p, i) { push(fromProductJs(p), ex.pinWhy[pinned[i]] || whyFrom(q, 'fallback')); }); /* then dictionary pins */
        for (var i = 1; i < res[0].length; i++) (function (xq) {
          res[0][i].forEach(function (p) { push(fromSuggest(p), ex.qWhy[xq] || whyFrom(xq, 'fallback')); });    /* then expansions */
        })(queries[i]);
        render(items.slice(0, 12), ex.links.slice(0, 3), q);
      });
    };
    if (DICT) go(); else (dictLoading || Promise.resolve()).then(go);
  }
  input.addEventListener('input', function () {
    clearTimeout(debounceT);
    var v = input.value;
    debounceT = setTimeout(function () { run(v); }, 220);
  });
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') {
      var first = resultsEl.querySelector('a.s3-sr');
      if (first) { e.preventDefault(); window.location.href = first.getAttribute('href'); }
      else if (norm(input.value)) { e.preventDefault(); window.location.href = '/search?q=' + encodeURIComponent(input.value) + '&type=product'; }
    }
  });
})();
